import { useEffect, useState } from "react";
import { PlusIcon, TrashIcon } from "@heroicons/react/outline";
import { getAllPixels } from "../../../../api/pixelsApi";
import { deleteEvent, updateEvent } from "../../../../api/eventsApi";
import ToastContainer from "../../../../components/toastContainer";
import Toast from "../../../../components/toast";
import PageLoader from "../../../../components/pageLoader";
import AddPixelModal from "./AddPixelModal";
import DeleteConfirmationModal from "../../../../components/deleteModal";

const MarketingTab = ({ eventId, data, onUpdate, isViewOnly }) => {
  
  const [pixels, setPixels] = useState(data.pixels);
  const [allPixels, setAllPixels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [pixelIds, setPixelIds] = useState([]);
  const [deletePixelId, setDeletePixelId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [showPixelModal, setShowPixelModal] = useState(false);

  // const fetchPixels = async () => {
  //   try {
  //     setLoading(true);
  //     const data = await getPixelsByEventId(eventId);
  //     setPixels(data.results || []);
  //   } catch (err) {
  //     setToast({
  //       type: "error",
  //       message:
  //         err?.response?.data?.detail ||
  //         "Something went wrong. Please try again.",
  //     });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchAllPixels = async () => {
    try {
      setLoading(true);
      const data = await getAllPixels();
      setAllPixels(data.results || []);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail ||
          "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllPixels();
  }, []);

  useEffect(() => {
    if (data?.pixels) {
      setPixelIds(data.pixels.map((p) => p.id));
    }
  }, [data]);

  // const handleAddPixels = async (values) => {
  //   values.forEach((val) => {
  //     const index = pixelIds.findIndex((a) => a === val.id);
  //     index === -1 && pixelIds.push(val.id);
  //   });
  //   try {
  //     setLoading(true);
  //     const updated = await updateEvent(eventId, {
  //       pixels: pixelIds,
  //     });
  //     setPixels(updated.pixels);
  //   } catch (err) {
  //     setToast({
  //       type: "error",
  //       message:
  //         err?.response?.data?.detail || err?.detail || "Failed to add pixels",
  //     });
  //   } finally {
  //     setShowPixelModal(false);
  //     setLoading(false);
  //   }
  // };

  const handleAddPixels = async (values) => {
    const newIds = values.map((v) => v.id);

    const updatedIds = [...new Set([...pixelIds, ...newIds])];

    try {
      setLoading(true);

      const updated = await updateEvent(eventId, {
        pixels: updatedIds,
      });

      setPixels(updated.pixels);
      onUpdate(updated);
      setPixelIds(updatedIds);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Failed to add pixels",
      });
    } finally {
      setShowPixelModal(false);
      setLoading(false);
    }
  };

  const handlePixelDelete = async () => {
    if (!deletePixelId) return;

    const updatedIds = pixelIds.filter((id) => id !== deletePixelId);
    try {
      setDeleting(true);

      const updated = await updateEvent(eventId, {
        pixels: updatedIds,
      });

      onUpdate(updated);
      setPixels(updated.pixels);
      setPixelIds(updatedIds);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail ||
          err?.detail ||
          "Failed to remove pixel",
      });
    } finally {
      setDeleting(false);
      setDeletePixelId(null);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="grid grid-cols-3 gap-4">
      {/* LEFT */}
      <div className="bg-[#131C31] border col-span-2 border-white/10 rounded-lg relative">
        <div className="flex items-center justify-between p-4">
          <div>
            <h1 className="text-m font-semibold text-white">Pixels</h1>
          </div>

          {/* hide Add Pixel button for viewers */}
          {!isViewOnly && (
            <button
              onClick={() => setShowPixelModal(true)}
              className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-3 py-1 rounded-lg text-sm font-medium transition"
            >
              <PlusIcon className="w-4 h-4" />
              Add Pixel
            </button>
          )}
        </div>
        <div className="bg-slate-900/60 border border-white/10 rounded-xl m-2">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b text-center border-white/10 text-left text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-6 py-4">Name</th>
                  <th className="px-6 py-4">Type</th>
                  <th className="px-6 py-4">ID</th>
                  <th className="px-6 py-4">Future Use</th>
                  {/* hide Actions column for viewers */}
                  {!isViewOnly && (
                    <th className="px-6 py-4 text-right">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {pixels.map((px) => (
                  <tr
                    key={px.id}
                    className="border-b text-center border-white/5 last:border-none hover:bg-slate-800/40 transition"
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-slate-300">
                        {px.name}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-slate-300">
                        {px.type}
                      </div>
                    </td>

                    <td className="px-6 py-4 text-sm text-slate-300">
                      {px.pixel_id}
                    </td>

                    <td className="px-6 py-4 text-sm text-slate-300">
                      {px.is_future_use === true ? "YES" : "NO"}
                    </td>

                    {/* hide delete button for viewers */}
                    {!isViewOnly && (
                      <td className="px-6 py-4">
                        <button
                          onClick={() => setDeletePixelId(px.id)}
                          className="p-2 rounded-md text-slate-300 hover:text-red-400 hover:bg-red-500/10 transition"
                          title="Remove"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty state placeholder (future) */}
          {pixels.length === 0 && (
            <div className="p-8 text-center text-slate-400">
              No pixels found.
            </div>
          )}
        </div>
      </div>

      {/* RIGHT */}
      <div className="bg-[#131C31] border border-white/10 rounded-lg p-4 relative">
        <h3 className="text-slate-400 mb-1">Whatsapp / Email Campaign</h3>
        <p className="text-slate-600">Section coming soon!</p>
      </div>

      {/* modals only for non-viewers */}
      {!isViewOnly && (
        <>
          <AddPixelModal
            isOpen={showPixelModal}
            onClose={() => setShowPixelModal(false)}
            onSubmit={handleAddPixels}
            allPixels={allPixels}
            selectedExisting={pixels}
          />

          <DeleteConfirmationModal
            isOpen={!!deletePixelId}
            description="Are you sure you want to remove this pixel."
            onClose={() => setDeletePixelId(null)}
            onConfirm={handlePixelDelete}
            loading={deleting}
          />
        </>
      )}

      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

export default MarketingTab;
